import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { WrappedHttpErrorResponse } from '@app/errors/wrapped-error';
import { PerforatingScheme } from '@app/shared/models/perforating-scheme';
import { TargetFormationCommonName } from '@app/shared/models/target-formation-common-name';
import {
  ArtificialLiftType, AssetStatus, AssetTeam, AssetType, BatchProcessType, CleanoutMethod,
  CompletionDesign, County, CurveBuild, DrillDesign, DrilloutMethod, FluidSystem, FracFleet, FracGroup, GelWeight, ProductionStatus,
  ProppantType, Rig, State, TargetFormation, Township, WellProfile, WellStatus
} from 'app/shared/models';
import { throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface IODataResult<T> {
  value: Array<T>;
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class GeneralAssetService {
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  private _cache = new Map<string, any>();

  get<T>(queryString: string, errorResponse: string): Promise<T[]> {
    if (this._cache.has(queryString)) {
      return Promise.resolve(this._cache.get(queryString));
    }

    return this.http.get<IODataResult<T>>(queryString).pipe(
      map<IODataResult<T>, T[]>(response => response.value),
      catchError(error => this.handleErrorObservable(error, 'Error getting artifical lift types')),).toPromise();
  }
  getArtificialLiftTypes(): Promise<ArtificialLiftType[]> {
    let queryString = `${this.environment.apiUrl}ArtificialLiftTypes?$select=ArtificialLiftTypeId,Description`;
    return this.get(queryString, 'Error getting artifical lift types');
  }

  getAssetStatuses(): Promise<AssetStatus[]> {
    let queryString = `${this.environment.apiUrl}AssetStatuses?$select=AssetStatusId,Description`;
    return this.get(queryString, 'Error getting asset status');
  }

  getAssetTypes(): Promise<AssetType[]> {
    let queryString = `${this.environment.apiUrl}AssetTypes?$select=AssetTypeId,Description`;
    return this.get(queryString, 'Error getting asset types');
  }

  getAssetTeams(): Promise<AssetTeam[]> {
    let queryString = `${this.environment.apiUrl}AssetTeams?$select=AssetTeamId,AssetTeamCode,Name`;
    return this.get(queryString, 'Error getting asset teams');
  }

  getBatchProcessTypes(assetTeamId: number): Promise<BatchProcessType[]> {
    let queryString = `${this.environment.apiUrl}BatchProcessTypes?$select=BatchProcessTypeId,Name&$filter=AssetTeamId eq ${assetTeamId}`;
    return this.get(queryString, 'Error getting batch process types');
  }

  getCleanoutMethods(): Promise<CleanoutMethod[]> {
    let queryString = `${this.environment.apiUrl}CleanoutMethods?$select=CleanoutMethodId,Description`;
    return this.get(queryString, 'Error getting clean out methods');
  }

  getCompletionDesigns(): Promise<CompletionDesign[]> {
    let select = 'ClustersPerStage,StageLength,FluidSystemId,CrossLinkPct,FluidQuantity,SlickWaterPct,LinearGelPct,ProppantPoundsPerFt,ProppantType1ItemId,ProppantType1Percent,ProppantType2ItemId,';
    let select2 = 'ProppantType2Percent,ProppantType3ItemId,ProppantType3Percent,ProppantType4ItemId,ProppantType4Percent,ProppantType5ItemId,ProppantType5Percent,PerforatingSchemeId,PerforationQuantity';
    let queryString = `${this.environment.apiUrl}CompletionDesigns?$select=CompletionDesignId,Name,${select}${select2}`;
    return this.get(queryString, 'Error getting completion designs');
  }

  getCounties(): Promise<County[]> {
    let queryString = `${this.environment.apiUrl}Counties?$select=CountyId,Description,StateId&$orderby=Description`;
    return this.get(queryString, 'Error getting countries');
  }

  getCurveBuilds(): Promise<CurveBuild[]> {
    let queryString = `${this.environment.apiUrl}CurveBuilds?$select=CurveBuildId,Description`;
    return this.get(queryString, 'Error getting curve builds');
  }

  getDrillDesigns(): Promise<DrillDesign[]> {
    let queryString = `${this.environment.apiUrl}DrillDesigns?$select=DrillDesignId,Name`;
    return this.get(queryString, 'Error getting drill designss');
  }

  getDrillOutMethods(): Promise<DrilloutMethod[]> {
    let queryString = `${this.environment.apiUrl}DrilloutMethods?$select=DrilloutMethodId,Description`;
    return this.get(queryString, 'Error getting drill out methods');
  }

  getFluidSystems(): Promise<FluidSystem[]> {
    let queryString = `${this.environment.apiUrl}FluidSystems?$select=FluidSystemId,Description`;
    return this.get(queryString, 'Error getting fluid systems');
  }

  getFracFleets(): Promise<FracFleet[]> {
    let queryString = `${this.environment.apiUrl}FracFleets?$select=FracFleetId,Name`;
    return this.get(queryString, 'Error getting frac fleets');
  }

  getFracGroups(): Promise<FracGroup[]> {
    let queryString = `${this.environment.apiUrl}FracGroups?$select=FracGroupId,Description`;
    return this.get(queryString, 'Error getting frac groups');
  }

  getGelWeights(): Promise<GelWeight[]> {
    let queryString = `${this.environment.apiUrl}GelWeights?$select=GelWeightId,Weight`;
    return this.get(queryString, 'Error getting gel weights');
  }

  getProductionStatuses(): Promise<ProductionStatus[]> {
    let queryString = `${this.environment.apiUrl}ProductionStatuses?$select=ProductionStatusId,Description`;
    return this.get(queryString, 'Error getting production statuses');
  }

  getProppantTypes(): Promise<ProppantType[]> {
    let queryString = `${this.environment.apiUrl}ProppantTypes?$select=ProppantTypeId,Description`;
    return this.get(queryString, 'Error getting proppant types');
  }

  getPerforationSchemes(): Promise<PerforatingScheme[]> {
    let queryString = `${this.environment.apiUrl}PerforatingSchemes?$select=PerforatingSchemeId,Abbreviation`;
    return this.get(queryString, 'Error getting proppant types');
  }

  getRigs(assetTeamId: number): Promise<Rig[]> {
    let queryString = `${this.environment.apiUrl}Rigs?$select=RigId,Name&$filter=AssetTeamId eq ${assetTeamId}`;
    return this.get(queryString, 'Error getting rigs');
  }

  getStates(): Promise<State[]> {
    let queryString = `${this.environment.apiUrl}States?$select=StateId`;
    return this.get(queryString, 'Error getting States');
  }

  getTargetFormations(): Promise<TargetFormation[]> {
    let queryString = `${this.environment.apiUrl}TargetFormations?$select=TargetFormationId,Description`;
    return this.get(queryString, 'Error getting target formations');
  }

  getTargetFormationWithAbbreviation(): Promise<any> {
    let queryString = `${this.environment.apiUrl}TargetFormations?$expand=TargetFormationCommonName($select=Abbreviation)&$select=TargetFormationId,Description,TargetFormationCommonNameId`;
    return this.get(queryString, 'Error getting target formations');
  }

  getTargetFormationCommonNames(): Promise<TargetFormationCommonName[]> {
    let queryString = `${this.environment.apiUrl}TargetFormationCommonNames?$select=TargetFormationCommonNameId,Abbreviation`;
    return this.get(queryString, 'Error getting target formation common names');
  }

  getTownships(): Promise<Township[]> {
    let queryString = `${this.environment.apiUrl}Townships?$select=TownshipId,Description`;
    return this.get(queryString, 'Error getting Townships');
  }

  getWellProfiles(): Promise<WellProfile[]> {
    let queryString = `${this.environment.apiUrl}WellProfiles?$select=WellProfileId,WellProfileName`;
    return this.get(queryString, 'Error getting Well profiles');
  }

  getWellStatuses(): Promise<WellStatus[]> {
    let queryString = `${this.environment.apiUrl}WellStatuses?$select=WellStatusId,Description`;
    return this.get(queryString, 'Error getting Well statuses');
  }

  private handleErrorObservable(error: Response | any, message: string) {
    return observableThrowError(new WrappedHttpErrorResponse(message, error));
  }
}